import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  CheckCircle,
  AlertCircle,
  Info,
  Loader2,
  Copy,
  Check,
} from "lucide-react";
import { getNotificationClasses, getColorScheme } from "../config/colors";
import JsonViewer from "./JsonViewer";
import { useTranslation } from "react-i18next";
import Alert from "./Alert";
import { getDirection, getTextAlign } from "../utils/rtl";

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message: string;
  type?: "loading" | "success" | "error" | "info" | "warning";
  showCloseButton?: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
  onContinue?: () => void;
  confirmText?: string;
  cancelText?: string;
  showButtons?: boolean;
  autoClose?: boolean;
  autoCloseDelay?: number;
  redirectUrl?: string;
  redirectDelay?: number;
  isDark?: boolean;
  jsonData?: any;
  showJsonData?: boolean;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
  type = "info",
  showCloseButton = true,
  onConfirm,
  onCancel,
  onContinue,
  confirmText = "OK",
  cancelText = "Cancel",
  showButtons = false,
  autoClose,
  autoCloseDelay,
  redirectUrl,
  redirectDelay = 2000,
  isDark = false,
  jsonData,
  showJsonData = false,
}) => {
  // Default autoClose to true for success messages, false otherwise
  const effectiveAutoClose =
    autoClose !== undefined ? autoClose : type === "success" ? true : false;

  // Use the passed autoCloseDelay or default to 3000
  const effectiveAutoCloseDelay =
    autoCloseDelay !== undefined ? autoCloseDelay : 3000;

  const { t, i18n } = useTranslation();
  const [copied, setCopied] = React.useState(false);
  const direction = getDirection(i18n.language);
  const textAlign = getTextAlign(i18n.language);

  // Auto-close effect - use ref to avoid dependency issues
  const onCloseRef = React.useRef(onClose);
  React.useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  React.useEffect(() => {
    if (!isOpen || !effectiveAutoClose) {
      return;
    }

    const timer = setTimeout(() => {
      onCloseRef.current();
    }, effectiveAutoCloseDelay);

    return () => {
      clearTimeout(timer);
    };
  }, [isOpen, effectiveAutoClose, effectiveAutoCloseDelay, type, autoClose]);

  React.useEffect(() => {
    if (isOpen && redirectUrl && effectiveAutoClose) {
      const timer = setTimeout(() => {
        window.location.href = redirectUrl;
      }, redirectDelay);
      return () => clearTimeout(timer);
    }
  }, [isOpen, redirectUrl, redirectDelay, effectiveAutoClose]);

  const getIcon = () => {
    // Keep loading aligned with info styling for consistent look
    const effectiveType = (type === "loading" ? "info" : type) as
      | "success"
      | "error"
      | "warning"
      | "info";
    const colors = getNotificationClasses(effectiveType, isDark);
    const colorClass =
      colors.split(" ").find((cls) => cls.startsWith("text-")) ||
      "text-gray-500";

    switch (type) {
      case "success":
        return <CheckCircle className={`w-8 h-8 ${colorClass}`} />;
      case "error":
        return <AlertCircle className={`w-8 h-8 ${colorClass}`} />;
      case "warning":
        return <AlertCircle className={`w-8 h-8 ${colorClass}`} />;
      case "loading":
        return <Loader2 className={`w-8 h-8 animate-spin ${colorClass}`} />;
      default:
        return <Info className={`w-8 h-8 ${colorClass}`} />;
    }
  };

  const getColors = () => {
    const effectiveType = (type === "loading" ? "info" : type) as
      | "success"
      | "error"
      | "warning"
      | "info";
    // getNotificationClasses already provides proper background colors
    return getNotificationClasses(effectiveType, isDark);
  };

  const getTextColor = () => {
    const effectiveType = (type === "loading" ? "info" : type) as
      | "success"
      | "error"
      | "warning"
      | "info";
    const colors = getNotificationClasses(effectiveType, isDark);
    return (
      colors.split(" ").find((cls) => cls.startsWith("text-")) ||
      "text-gray-700 dark:text-gray-300"
    );
  };

  const getThemeButtonColors = () => {
    const scheme = getColorScheme();
    const primaryColor = scheme.primaryShades[500];
    const primaryHoverColor = scheme.primaryShades[600];

    return {
      bg: `bg-${primaryColor}`,
      hover: `hover:bg-${primaryHoverColor}`,
    };
  };

  const copyToClipboard = async () => {
    if (jsonData) {
      try {
        await navigator.clipboard.writeText(JSON.stringify(jsonData, null, 2));
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      } catch (err) {
        console.error("Failed to copy JSON:", err);
      }
    }
  };

  const colors = getColors();
  const textColor = getTextColor();
  const buttonColors = getThemeButtonColors();

  // Use Alert component for error/warning confirmations (with buttons)
  // For warning/error types, use Alert if there are buttons OR if onConfirm/onCancel are provided
  const useAlert =
    (type === "error" || type === "warning") &&
    (showButtons === true || onConfirm !== undefined || onCancel !== undefined);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={showCloseButton ? onClose : undefined}
          />

          {/* Modal / Alert */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" dir={direction}>
            {useAlert ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.2 }}
                className="relative w-full max-w-md mx-4"
                onClick={(e) => e.stopPropagation()}
                dir={direction}
              >
                <Alert
                  type={type === "error" ? "error" : "warning"}
                  title={title}
                  message={message}
                  isDark={isDark}
                  onClose={showCloseButton ? onClose : undefined}
                  onConfirm={onConfirm}
                  onCancel={onCancel}
                  confirmText={confirmText}
                  cancelText={cancelText}
                  showCloseButton={showCloseButton}
                />
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.2 }}
                className={`relative w-full max-w-md rounded-lg border shadow-xl ${colors}`}
                onClick={(e) => e.stopPropagation()}
                dir={direction}
              >
                {/* Header */}
                <div className={`flex items-center justify-between p-6 pb-4`}>
                  <div className={`flex items-center gap-3`} style={{ flexDirection: direction === 'rtl' ? 'row-reverse' : 'row' }}>
                    {getIcon()}
                    {title && (
                      <h3 className={`text-lg font-semibold ${colors}`} style={{ textAlign }}>
                        {title}
                      </h3>
                    )}
                  </div>
                  {showCloseButton && (
                    <button
                      onClick={onClose}
                      className={`p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors ${direction === 'rtl' ? 'ml-auto' : 'mr-auto'}`}
                    >
                      <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    </button>
                  )}
                </div>

                {/* Content */}
                <div className="px-6 pb-6">
                  <p className={`text-sm ${textColor} leading-relaxed`} style={{ textAlign }}>
                    {message}
                  </p>

                  {/* JSON Data Display */}
                  {showJsonData && jsonData && (
                    <div className="mt-4">
                      <h4 className={`text-sm font-medium ${textColor} mb-2`}>
                        {t("notifications.jsonPayloadTitle")}:
                      </h4>
                      <div className="relative">
                        <JsonViewer data={jsonData} isDark={isDark} />
                        <button
                          onClick={copyToClipboard}
                          className={`absolute top-2 p-2 rounded-lg transition-all duration-200 ${
                            direction === 'rtl' ? 'left-2' : 'right-2'
                          } ${
                            copied
                              ? "bg-green-500/20 text-green-600 dark:text-green-400"
                              : "bg-transparent text-gray-600 hover:bg-gray-500/10 dark:text-gray-300 dark:hover:bg-gray-400/10"
                          }`}
                          title={copied ? t("common.copied") : t("common.copy")}
                        >
                          {copied ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>

                      {/* Continue button when custom continuation is provided */}
                      {onContinue && !effectiveAutoClose && (
                        <div className={`mt-4 flex ${direction === 'rtl' ? 'justify-start' : 'justify-end'}`}>
                          <button
                            onClick={() => {
                              onContinue();
                            }}
                            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors text-white ${buttonColors.bg} ${buttonColors.hover}`}
                          >
                            {direction === 'rtl' ? `← ${t("common.continue")}` : `${t("common.continue")} →`}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Redirect indicator */}
                  {redirectUrl && autoClose && (
                    <div className="mt-4 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        Redirecting in {redirectDelay / 1000} seconds...
                      </p>
                    </div>
                  )}

                  {/* Buttons */}
                  {showButtons && (
                    <div className={`flex gap-3 mt-6`} style={{ flexDirection: direction === 'rtl' ? 'row-reverse' : 'row' }}>
                      {onCancel && (
                        <button
                          onClick={onCancel}
                          className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                          style={{ textAlign }}
                        >
                          {cancelText}
                        </button>
                      )}
                      {onConfirm && (
                        <button
                          onClick={onConfirm}
                          className={`flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
                            type === "error"
                              ? "bg-red-500 hover:bg-red-600"
                              : type === "success"
                              ? "bg-emerald-500 hover:bg-emerald-600"
                              : type === "warning"
                              ? `${buttonColors.bg} ${buttonColors.hover}`
                              : `${buttonColors.bg} ${buttonColors.hover}`
                          }`}
                          style={{ textAlign }}
                        >
                          {confirmText}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default Modal;
